import { forwardRef } from 'react'
import { motion } from 'framer-motion'

const variants = {
  primary: 'bg-pp-ink text-pp-ivory hover:bg-pp-charcoal',
  secondary: 'bg-pp-linen text-pp-ink border border-pp-dune hover:bg-pp-sand',
  ghost: 'bg-transparent text-pp-ink hover:bg-pp-linen',
  outline: 'bg-transparent text-pp-ink border border-pp-ink hover:bg-pp-ink hover:text-pp-ivory',
}

const sizes = {
  sm: 'px-4 py-2 text-xs',
  md: 'px-6 py-3 text-sm',
  lg: 'px-8 py-4 text-base',
}

const Button = forwardRef(({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  isLoading = false,
  icon,
  iconPosition = 'left',
  as: Component = 'button',
  ...props
}, ref) => {
  const isButton = Component === 'button'
  const MotionComponent = isButton ? motion.button : motion(Component)

  const baseStyles = 'inline-flex items-center justify-center gap-2 rounded-xl font-body font-medium tracking-wide transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed'

  const isDisabled = disabled || loading || isLoading

  return (
    <MotionComponent
      ref={ref}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={isButton ? isDisabled : undefined}
      whileHover={{ scale: isDisabled ? 1 : 1.02 }}
      whileTap={{ scale: isDisabled ? 1 : 0.98 }}
      transition={{ duration: 0.2 }}
      {...props}
    >
      {(loading || isLoading) ? (
        <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : (
        <>
          {icon && iconPosition === 'left' && <span className="w-4 h-4">{icon}</span>}
          {children}
          {icon && iconPosition === 'right' && <span className="w-4 h-4">{icon}</span>}
        </>
      )}
    </MotionComponent>
  )
})

Button.displayName = 'Button'

export default Button
